// Person drill-down — a side sheet with one person's work summary, their
// workload band (+ reasons) and the Needs Attention list. Everything comes
// from GET /workload/people/{id}; `item_visibility: none` → counts only.
// Phase 2: a Capacity block (units / load / headroom / utilization) when the
// person's capacity is configured, "Capacity not set" otherwise — hidden when
// the policy turns utilization off.
// Phase 3: a "Last 30 days" trend strip (Active, Overdue, Load vs capacity).
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Loader2 } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/features/shared/components/ui/sheet';
import { getApiErrorMessage } from '@/lib/apiError';
import { usePersonWorkload } from '@/features/work/api/workloadHooks';
import type { StaleBasis, WorkloadPerson } from '@/services/workloadService';
import { CapacityBlock } from './capacity';
import { BandChip } from './health';
import { NeedsAttention } from './NeedsAttention';
import { SummaryTiles } from './tiles';
import { TrendStrip } from './trends';

export const PersonDrilldown: React.FC<{
  employeeId: string | null;
  /** Row data for an instant header while the detail loads. */
  person?: WorkloadPerson | null;
  onClose: () => void;
  staleAfterDays?: number;
  staleBasis?: StaleBasis;
  dueSoonDays?: number;
  /** Policy `workload_show_utilization` (default true). */
  showUtilization?: boolean;
}> = ({ employeeId, person, onClose, staleAfterDays, staleBasis, dueSoonDays, showUtilization = true }) => {
  const { t } = useTranslation('work');
  const { data, loading, error } = usePersonWorkload(employeeId);
  useEffect(() => {
    if (error) toast.error(getApiErrorMessage(error, t('person_load_failed', { defaultValue: 'Could not load this person’s workload.' })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);
  const p = data?.person || person;
  const subline = p ? [p.job_title, p.department_name].filter(Boolean).join(' · ') : '';
  const hideTitles = (data?.item_visibility || 'none') === 'none';

  return (
    <Sheet open={!!employeeId} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-xl w-full overflow-y-auto" data-testid="person-drilldown">
        <SheetHeader>
          <SheetTitle>{p?.display_name || t('person', { defaultValue: 'Person' })}</SheetTitle>
          <SheetDescription>{subline || t('person_workload_sub', { defaultValue: 'Work summary and items that need attention.' })}</SheetDescription>
        </SheetHeader>
        <div className="mt-4 space-y-4">
          {loading && !data && (
            <p className="text-sm text-muted-foreground flex items-center gap-2" role="status">
              <Loader2 className="w-4 h-4 animate-spin" /> {t('common.loading', { defaultValue: 'Loading…' })}
            </p>
          )}
          {data && (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <BandChip band={data.workload.band} reasons={data.workload.reasons} confidence={data.workload.confidence} />
                {data.summary.paused > 0 && (
                  <span className="text-xs text-muted-foreground">
                    {t('paused_count', { defaultValue: '{{count}} on hold', count: data.summary.paused })}
                  </span>
                )}
                {data.summary.collaborating > 0 && (
                  <span className="text-xs text-muted-foreground">
                    · {t('collaborating_count', { defaultValue: 'collaborating on {{count}}', count: data.summary.collaborating })}
                  </span>
                )}
              </div>
              <SummaryTiles totals={data.summary} compact staleAfterDays={staleAfterDays} staleBasis={staleBasis} dueSoonDays={dueSoonDays} />
              {showUtilization && <CapacityBlock capacity={data.capacity} />}
              <TrendStrip employeeId={employeeId} showCapacity={showUtilization} testId="person-trends" />
              <NeedsAttention items={data.attention} hideTitles={hideTitles} filterable={!hideTitles} />
              {data.item_visibility === 'partial' && (
                <p className="text-xs text-muted-foreground">{t('visibility_partial', { defaultValue: 'Some work items are outside your visibility scope and are counted but not listed.' })}</p>
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PersonDrilldown;
